// ─────────────────────────────────────────────────────────────────────────────
// forecastDeck.js — professional PowerPoint (.pptx) report for the Sales Forecast
// and dashboard. Starts with an executive dashboard, then moves to details:
// baseline vs rolling vs actual vs sales-orders, monthly trend, accounts, inventory.
// Generated client-side from the live CRM, mirroring the Excel report data.
// ─────────────────────────────────────────────────────────────────────────────
import pptxgen from 'pptxgenjs';
import crmApi from './crmApi';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const r3 = n => Math.round((Number(n) || 0) * 1000) / 1000;
const fmtNum = (n) => { const v = r3(n); const f = Math.abs(v % 1) > 0.0005; return v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: f ? 3 : 0 }); };

// CRM teal palette
const C = {
  ink:   '0F2E2B',   // deep teal-black (headings)
  teal:  '0D9488',   // brand teal
  tealLt:'D6F0EC',   // pale teal fill
  blue:  '2563EB',
  green: '16A34A',
  amber: 'D97706',
  red:   'DC2626',
  purple:'7C3AED',
  line:  'D9E2E0',
  muted: '6B7B78',
  white: 'FFFFFF',
  trackBg:'EEF3F2',
};
const FONT = 'Arial';

function cellMetrics(v, pricing) {
  const u = Number(v.amount) || 0;
  if (v.frozen && v.snap_revenue != null && Number(v.snap_revenue) !== 0)
    return { units: u, cost: r3(u * Number(v.snap_cost || 0)), sales: r3(u * Number(v.snap_price || 0)), rev: Number(v.snap_revenue) };
  const p = pricing[v.category_id] || { price: 0, cost: 0, margin: 0 };
  return { units: u, cost: r3(u * p.cost), sales: r3(u * p.price), rev: r3(u * (p.margin != null ? p.margin : p.price - p.cost)) };
}
// product → { units, cost, sales, rev } totals
function productTotals(values, pricing, catName) {
  const byCat = {};
  (values || []).forEach(v => {
    if (!v.category_id) return;
    const x = cellMetrics(v, pricing);
    const k = v.category_id;
    byCat[k] = byCat[k] || { name: catName(k), units: 0, cost: 0, sales: 0, rev: 0 };
    byCat[k].units += x.units; byCat[k].cost += x.cost; byCat[k].sales += x.sales; byCat[k].rev += x.rev;
  });
  return Object.values(byCat).map(o => ({ ...o, units: r3(o.units), cost: r3(o.cost), sales: r3(o.sales), rev: r3(o.rev) }))
    .sort((a, b) => b.rev - a.rev);
}
function monthlyTotals(values, pricing) {
  const m = Array.from({ length: 12 }, () => ({ units: 0, cost: 0, sales: 0, rev: 0 }));
  (values || []).forEach(v => { const x = cellMetrics(v, pricing); const i = (v.month || 1) - 1; if (i >= 0 && i < 12) { m[i].units += x.units; m[i].cost += x.cost; m[i].sales += x.sales; m[i].rev += x.rev; } });
  return m.map(o => ({ units: r3(o.units), cost: r3(o.cost), sales: r3(o.sales), rev: r3(o.rev) }));
}
const hdr = (align) => ({ bold: true, color: 'FFFFFF', fill: { color: C.ink }, fontSize: 10, align: align || 'left', valign: 'middle' });
const cell = (align, color) => ({ fontSize: 10, align: align || 'left', valign: 'middle', color: color || '222222' });

export async function downloadForecastDeck(year) {
  const [analytics, cats, accounts, pricing, forecasts, soGrid, products] = await Promise.all([
    crmApi.get(`/forecast-analytics/${year}`).catch(() => null),
    crmApi.get('/categories').catch(() => []),
    crmApi.get('/accounts').catch(() => []),
    crmApi.get('/category-pricing').catch(() => ({})),
    crmApi.get(`/forecasts?year=${year}`).catch(() => []),
    crmApi.get(`/forecast-sales-orders/${year}`).catch(() => null),
    crmApi.get('/products').catch(() => []),
  ]);
  const catName = id => (cats.find(c => c.id === id) || {}).name || 'Product';

  const baseline = forecasts.find(f => f.forecast_type === 'target' && f.is_active)
    || forecasts.find(f => f.forecast_type === 'target' && f.status === 'approved')
    || forecasts.find(f => f.forecast_type === 'target');
  const rolling = forecasts.find(f => f.forecast_type === 'forecast');
  const actual = forecasts.find(f => f.forecast_type === 'actual');
  const loadVals = async (f) => f ? ((await crmApi.get(`/forecasts/${f.id}`)).values || []) : [];
  const [bVals, rVals, aVals] = await Promise.all([loadVals(baseline), loadVals(rolling), loadVals(actual)]);
  const soVals = (soGrid && soGrid.values) || [];
  const soPricing = (soGrid && soGrid.pricing) || pricing;

  const t = (analytics && analytics.totals) || { target: 0, forecast: 0, performance: 0 };
  const v = (analytics && analytics.variance) || {};
  const genDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

  const p = new pptxgen();
  p.defineLayout({ name: 'WIDE', width: 13.333, height: 7.5 });
  p.layout = 'WIDE';
  p.theme = { headFontFace: FONT, bodyFontFace: FONT };
  const W = 13.333, H = 7.5;

  const addBase = (title, subtitle) => {
    const slide = p.addSlide();
    slide.background = { color: C.white };
    slide.addShape(p.ShapeType.rect, { x: 0, y: 0, w: W, h: 0.08, fill: { color: C.teal } });
    if (title) {
      slide.addText(title, { x: 0.55, y: 0.3, w: W - 1.1, h: 0.55, fontFace: FONT, fontSize: 24, bold: true, color: C.ink });
      if (subtitle) slide.addText(subtitle, { x: 0.55, y: 0.84, w: W - 1.1, h: 0.32, fontFace: FONT, fontSize: 12, color: C.muted });
      slide.addShape(p.ShapeType.line, { x: 0.55, y: subtitle ? 1.2 : 0.95, w: W - 1.1, h: 0, line: { color: C.line, width: 1 } });
    }
    slide.addText(`Sales Forecast Report  ·  ${year}  ·  ${genDate}`, { x: 0.55, y: H - 0.42, w: 9, h: 0.3, fontFace: FONT, fontSize: 8, color: C.muted });
    slide.addText('CONFIDENTIAL', { x: W - 2.55, y: H - 0.42, w: 2, h: 0.3, align: 'right', fontFace: FONT, fontSize: 8, color: C.muted });
    return slide;
  };

  // ── Slide 1: Title ───────────────────────────────────────────────────────
  {
    const slide = p.addSlide();
    slide.background = { color: C.white };
    slide.addShape(p.ShapeType.rect, { x: 0, y: 0, w: W, h: 2.8, fill: { color: C.ink } });
    slide.addShape(p.ShapeType.rect, { x: 0, y: 2.8, w: W, h: 0.12, fill: { color: C.teal } });
    slide.addText('ANNUAL SALES FORECAST', { x: 0.7, y: 0.95, w: W - 1.4, h: 0.4, fontFace: FONT, fontSize: 14, color: '9FD6CF', charSpacing: 3, bold: true });
    slide.addText(`Forecast & Performance Review · ${year}`, { x: 0.7, y: 1.42, w: W - 1.4, h: 0.9, fontFace: FONT, fontSize: 32, bold: true, color: C.white });
    slide.addText([
      { text: `${fmtNum(t.performance)}`, options: { fontSize: 54, bold: true, color: C.ink } },
    ], { x: 0.7, y: 3.5, w: 6, h: 1.1, fontFace: FONT });
    slide.addText('ACTUAL REVENUE TO DATE (OMR)', { x: 0.74, y: 4.62, w: 6, h: 0.3, fontFace: FONT, fontSize: 11, color: C.muted, charSpacing: 1 });
    const meta = [
      ['Report date', genDate],
      ['Baseline plan', (analytics && analytics.target_name) || (baseline ? 'Approved baseline' : '—')],
      ['Forecast accuracy', `${(analytics && analytics.accuracy_pct) || 0}%`],
      ['Customers', String(accounts.length)],
      ['Products', String(products.length)],
    ];
    slide.addText(
      meta.map(([k, val]) => ([
        { text: `${k}   `, options: { fontSize: 12, color: C.muted, bold: true } },
        { text: `${val}\n`, options: { fontSize: 12, color: C.ink } },
      ])).flat(),
      { x: 7.2, y: 3.45, w: 5.4, h: 2.4, fontFace: FONT, lineSpacingMultiple: 1.5 }
    );
  }

  // ── Slide 2: Executive Dashboard ─────────────────────────────────────────
  {
    const slide = addBase('Executive Dashboard', `Forecast position for ${year}`);
    const kpis = [
      { label: 'Baseline Revenue', value: fmtNum(t.target), color: C.blue },
      { label: 'Rolling Revenue', value: fmtNum(t.forecast), color: C.purple },
      { label: 'Actual Revenue', value: fmtNum(t.performance), color: C.green },
      { label: 'Accuracy', value: `${(analytics && analytics.accuracy_pct) || 0}%`, color: C.teal },
    ];
    const cardW = 2.92, gap = 0.25, startX = 0.55, cardY = 1.5, cardH = 1.5;
    kpis.forEach((k, i) => {
      const x = startX + i * (cardW + gap);
      slide.addShape(p.ShapeType.roundRect, { x, y: cardY, w: cardW, h: cardH, rectRadius: 0.08, fill: { color: C.tealLt }, line: { color: C.line, width: 1 } });
      slide.addText(k.value, { x, y: cardY + 0.24, w: cardW, h: 0.7, align: 'center', fontFace: FONT, fontSize: 26, bold: true, color: k.color });
      slide.addText(k.label.toUpperCase(), { x, y: cardY + 1.0, w: cardW, h: 0.35, align: 'center', fontFace: FONT, fontSize: 9.5, color: C.muted, charSpacing: 1 });
    });

    // Variance strip
    const vY = 3.35;
    const vars = [
      ['Rolling vs Baseline', v.forecast_vs_target || 0],
      ['Actual vs Baseline', v.perf_vs_target || 0],
      ['Actual vs Rolling', v.perf_vs_forecast || 0],
    ];
    const vW = 3.94;
    vars.forEach(([lab, val], i) => {
      const x = 0.55 + i * (vW + 0.2);
      slide.addShape(p.ShapeType.roundRect, { x, y: vY, w: vW, h: 0.85, rectRadius: 0.06, fill: { color: C.white }, line: { color: C.line, width: 1 } });
      slide.addText(lab.toUpperCase(), { x: x + 0.2, y: vY + 0.12, w: vW - 0.4, h: 0.3, fontFace: FONT, fontSize: 9, color: C.muted, charSpacing: 1 });
      slide.addText(`${val >= 0 ? '+' : ''}${fmtNum(val)}`, { x: x + 0.2, y: vY + 0.38, w: vW - 0.4, h: 0.4, fontFace: FONT, fontSize: 18, bold: true, color: val >= 0 ? C.green : C.red });
    });

    // Monthly comparison mini-chart (baseline/rolling/actual)
    slide.addText('MONTHLY COMPARISON', { x: 0.55, y: 4.5, w: 6, h: 0.3, fontFace: FONT, fontSize: 11, bold: true, color: C.teal, charSpacing: 1 });
    const months = (analytics && analytics.months) || [];
    const chartX = 0.7, chartY = 4.95, chartW = 12.0, chartH = 1.7;
    const maxV = Math.max(1, ...months.flatMap(m => [m.target, m.forecast, m.performance]));
    const slot = chartW / 12;
    months.forEach((m, i) => {
      const gx = chartX + i * slot;
      const bars = [['target', C.blue], ['forecast', C.purple], ['performance', C.green]];
      const bw = slot / 4.2;
      bars.forEach(([k, col], bi) => {
        const val = m[k] || 0; const bh = (val / maxV) * chartH;
        slide.addShape(p.ShapeType.rect, { x: gx + 0.12 + bi * bw, y: chartY + chartH - bh, w: bw - 0.03, h: Math.max(0.02, bh), fill: { color: col }, line: { type: 'none' } });
      });
      slide.addText(MONTHS[i], { x: gx, y: chartY + chartH + 0.04, w: slot, h: 0.22, align: 'center', fontFace: FONT, fontSize: 7.5, color: C.muted });
    });
    slide.addText([
      { text: '■ ', options: { color: C.blue } }, { text: 'Baseline    ', options: { color: C.muted } },
      { text: '■ ', options: { color: C.purple } }, { text: 'Rolling    ', options: { color: C.muted } },
      { text: '■ ', options: { color: C.green } }, { text: 'Actual', options: { color: C.muted } },
    ], { x: 0.7, y: H - 0.72, w: 8, h: 0.25, fontFace: FONT, fontSize: 9 });
  }

  // ── Detail slide: a product table for a given dataset ─────────────────────
  const addProductSlide = (title, subtitle, values, pr, accent) => {
    const slide = addBase(title, subtitle);
    const prods = productTotals(values, pr, catName);
    const tot = prods.reduce((a, x) => { a.units += x.units; a.cost += x.cost; a.sales += x.sales; a.rev += x.rev; return a; }, { units: 0, cost: 0, sales: 0, rev: 0 });
    // total strip
    slide.addShape(p.ShapeType.roundRect, { x: 0.55, y: 1.45, w: 12.23, h: 0.6, rectRadius: 0.06, fill: { color: C.tealLt }, line: { color: C.line, width: 1 } });
    slide.addText([
      { text: 'Units ', options: { fontSize: 10, color: C.muted } }, { text: `${fmtNum(tot.units)}      `, options: { fontSize: 13, bold: true, color: C.ink } },
      { text: 'Cost ', options: { fontSize: 10, color: C.muted } }, { text: `${fmtNum(tot.cost)}      `, options: { fontSize: 13, bold: true, color: C.amber } },
      { text: 'Sales ', options: { fontSize: 10, color: C.muted } }, { text: `${fmtNum(tot.sales)}      `, options: { fontSize: 13, bold: true, color: C.blue } },
      { text: 'Revenue ', options: { fontSize: 10, color: C.muted } }, { text: `${fmtNum(tot.rev)}`, options: { fontSize: 13, bold: true, color: C.green } },
    ], { x: 0.75, y: 1.45, w: 11.8, h: 0.6, valign: 'middle', fontFace: FONT });

    const rows = [[
      { text: 'Product', options: hdr() }, { text: 'Units', options: hdr('right') },
      { text: 'Cost', options: hdr('right') }, { text: 'Sales', options: hdr('right') }, { text: 'Revenue', options: hdr('right') },
    ]];
    prods.slice(0, 12).forEach(x => rows.push([
      { text: x.name, options: cell() }, { text: fmtNum(x.units), options: cell('right') },
      { text: fmtNum(x.cost), options: cell('right', C.amber) }, { text: fmtNum(x.sales), options: cell('right', C.blue) }, { text: fmtNum(x.rev), options: cell('right', C.green) },
    ]));
    if (prods.length === 0) rows.push([{ text: 'No data for this view yet.', options: { ...cell(), colspan: 5, align: 'center', color: C.muted } }]);
    slide.addTable(rows, { x: 0.55, y: 2.25, w: 12.23, colW: [5.43, 1.7, 1.7, 1.7, 1.7], border: { type: 'solid', color: C.line, pt: 0.5 }, rowH: 0.34, valign: 'middle' });
    if (prods.length > 12) slide.addText(`+ ${prods.length - 12} more products`, { x: 0.55, y: H - 0.75, w: 6, h: 0.25, fontFace: FONT, fontSize: 8, italic: true, color: C.muted });
    return slide;
  };

  addProductSlide('Baseline Forecast', `Approved annual plan · ${year}`, bVals, pricing, C.blue);
  addProductSlide('Rolling Forecast', `Working forecast · ${year}`, rVals, pricing, C.purple);
  addProductSlide('Actual Sales Performance', `Verified from frozen months · ${year}`, aVals, pricing, C.green);
  addProductSlide('Service Orders', `Confirmed & approved service orders · ${year}`, soVals, soPricing, C.teal);

  // ── Plan vs Actual comparison (by product) ───────────────────────────────
  {
    const slide = addBase('Plan vs Actual — by product', `Baseline plan against actual sales · ${year}`);
    const bp = productTotals(bVals, pricing, catName);
    const ap = productTotals(aVals, pricing, catName);
    const byName = {};
    bp.forEach(x => { byName[x.name] = byName[x.name] || { name: x.name, base: 0, act: 0 }; byName[x.name].base = x.rev; });
    ap.forEach(x => { byName[x.name] = byName[x.name] || { name: x.name, base: 0, act: 0 }; byName[x.name].act = x.rev; });
    const rowsData = Object.values(byName).sort((a, b) => b.base - a.base);
    const rows = [[
      { text: 'Product', options: hdr() }, { text: 'Baseline Rev.', options: hdr('right') },
      { text: 'Actual Rev.', options: hdr('right') }, { text: 'Variance', options: hdr('right') }, { text: 'Attainment', options: hdr('right') },
    ]];
    rowsData.slice(0, 12).forEach(x => {
      const diff = r3(x.act - x.base); const att = x.base > 0 ? Math.round(x.act / x.base * 100) : (x.act > 0 ? 100 : 0);
      rows.push([
        { text: x.name, options: cell() }, { text: fmtNum(x.base), options: cell('right', C.blue) },
        { text: fmtNum(x.act), options: cell('right', C.green) },
        { text: `${diff >= 0 ? '+' : ''}${fmtNum(diff)}`, options: cell('right', diff >= 0 ? C.green : C.red) },
        { text: `${att}%`, options: cell('right', C.ink) },
      ]);
    });
    if (rowsData.length === 0) rows.push([{ text: 'No comparison data yet.', options: { ...cell(), colspan: 5, align: 'center', color: C.muted } }]);
    slide.addTable(rows, { x: 0.55, y: 1.55, w: 12.23, colW: [4.63, 2.0, 2.0, 2.1, 1.5], border: { type: 'solid', color: C.line, pt: 0.5 }, rowH: 0.36, valign: 'middle' });
  }

  // ── Accounts: count + growth ─────────────────────────────────────────────
  {
    const slide = addBase('Customer Accounts', 'Portfolio and month-over-month growth');
    slide.addShape(p.ShapeType.roundRect, { x: 0.55, y: 1.45, w: 12.23, h: 0.6, rectRadius: 0.06, fill: { color: C.tealLt }, line: { color: C.line, width: 1 } });
    const totDeals = accounts.reduce((s, a) => s + (Number(a.deal_count) || 0), 0);
    const totContacts = accounts.reduce((s, a) => s + (Number(a.contact_count) || 0), 0);
    slide.addText([
      { text: 'Accounts ', options: { fontSize: 10, color: C.muted } }, { text: `${accounts.length}      `, options: { fontSize: 13, bold: true, color: C.ink } },
      { text: 'Deals ', options: { fontSize: 10, color: C.muted } }, { text: `${totDeals}      `, options: { fontSize: 13, bold: true, color: C.teal } },
      { text: 'Contacts ', options: { fontSize: 10, color: C.muted } }, { text: `${totContacts}`, options: { fontSize: 13, bold: true, color: C.blue } },
    ], { x: 0.75, y: 1.45, w: 11.8, h: 0.6, valign: 'middle', fontFace: FONT });

    // month-over-month growth
    const byMonth = {};
    accounts.forEach(a => { if (!a.created_at) return; const d = new Date(a.created_at); const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; byMonth[k] = (byMonth[k] || 0) + 1; });
    const keys = Object.keys(byMonth).sort().slice(-12);
    let cum = accounts.filter(a => { if (!a.created_at) return false; const k = `${new Date(a.created_at).getFullYear()}-${String(new Date(a.created_at).getMonth() + 1).padStart(2, '0')}`; return k < (keys[0] || ''); }).length;
    const rows = [[
      { text: 'Month', options: hdr() }, { text: 'New Accounts', options: hdr('right') },
      { text: 'Cumulative', options: hdr('right') }, { text: 'Growth', options: hdr('right') },
    ]];
    keys.forEach(k => { const added = byMonth[k]; const prev = cum; cum += added; const g = prev > 0 ? Math.round(added / prev * 100) : (added > 0 ? 100 : 0); rows.push([
      { text: k, options: cell() }, { text: String(added), options: cell('right', C.green) },
      { text: String(cum), options: cell('right', C.ink) }, { text: `${g >= 0 ? '+' : ''}${g}%`, options: cell('right', C.teal) },
    ]); });
    if (keys.length === 0) rows.push([{ text: 'No account creation dates recorded.', options: { ...cell(), colspan: 4, align: 'center', color: C.muted } }]);
    slide.addTable(rows, { x: 0.55, y: 2.25, w: 12.23, colW: [4.73, 2.5, 2.5, 2.5], border: { type: 'solid', color: C.line, pt: 0.5 }, rowH: 0.34, valign: 'middle' });
  }

  // ── Inventory ────────────────────────────────────────────────────────────
  {
    const slide = addBase('Inventory', 'Stock position by product');
    const totVal = products.reduce((s, pr) => s + (Number(pr.stock_qty) || 0) * (Number(pr.cost_price) || 0), 0);
    const low = products.filter(pr => (Number(pr.reorder_level) || 0) > 0 && (Number(pr.stock_qty) || 0) <= (Number(pr.reorder_level) || 0));
    slide.addShape(p.ShapeType.roundRect, { x: 0.55, y: 1.45, w: 12.23, h: 0.6, rectRadius: 0.06, fill: { color: C.tealLt }, line: { color: C.line, width: 1 } });
    slide.addText([
      { text: 'Products ', options: { fontSize: 10, color: C.muted } }, { text: `${products.length}      `, options: { fontSize: 13, bold: true, color: C.ink } },
      { text: 'Stock value ', options: { fontSize: 10, color: C.muted } }, { text: `${fmtNum(totVal)}      `, options: { fontSize: 13, bold: true, color: C.green } },
      { text: 'Low stock ', options: { fontSize: 10, color: C.muted } }, { text: `${low.length}`, options: { fontSize: 13, bold: true, color: low.length ? C.red : C.muted } },
    ], { x: 0.75, y: 1.45, w: 11.8, h: 0.6, valign: 'middle', fontFace: FONT });

    const rows = [[
      { text: 'Product', options: hdr() }, { text: 'Category', options: hdr() }, { text: 'Stock', options: hdr('right') },
      { text: 'Reorder', options: hdr('right') }, { text: 'Unit Cost', options: hdr('right') }, { text: 'Stock Value', options: hdr('right') },
    ]];
    [...products].sort((a, b) => (Number(b.stock_qty) || 0) * (Number(b.cost_price) || 0) - (Number(a.stock_qty) || 0) * (Number(a.cost_price) || 0)).slice(0, 12).forEach(pr => {
      const stock = Number(pr.stock_qty) || 0, reorder = Number(pr.reorder_level) || 0, cost = Number(pr.cost_price) || 0;
      const isLow = reorder > 0 && stock <= reorder;
      rows.push([
        { text: pr.name, options: cell() }, { text: pr.category_name || '—', options: cell('left', C.muted) },
        { text: fmtNum(stock), options: cell('right', isLow ? C.red : '222222') }, { text: fmtNum(reorder), options: cell('right', C.muted) },
        { text: fmtNum(cost), options: cell('right', C.amber) }, { text: fmtNum(r3(stock * cost)), options: cell('right', C.green) },
      ]);
    });
    if (products.length === 0) rows.push([{ text: 'No products recorded.', options: { ...cell(), colspan: 6, align: 'center', color: C.muted } }]);
    slide.addTable(rows, { x: 0.55, y: 2.25, w: 12.23, colW: [3.93, 2.5, 1.45, 1.45, 1.45, 1.45], border: { type: 'solid', color: C.line, pt: 0.5 }, rowH: 0.34, valign: 'middle' });
    if (products.length > 12) slide.addText(`+ ${products.length - 12} more products`, { x: 0.55, y: H - 0.75, w: 6, h: 0.25, fontFace: FONT, fontSize: 8, italic: true, color: C.muted });
  }

  await p.writeFile({ fileName: `Sales_Forecast_Report_${year}_${new Date().toISOString().slice(0, 10)}.pptx` });
}
